import { Media } from '../models';

export enum MediaType {
    Photo = 'photo',
    Video = 'video',
}

const COMMON_VIDEO_EXTENSIONS = [
    '.3gp',
    '.3g2',
    '.avi',
    '.f4v',
    '.flv',
    '.h264',
    '.m4p',
    '.m4v',
    '.mkv',
    '.mov',
    '.mp2',
    '.mp4',
    '.mpe',
    '.mpeg',
    '.mpg',
    '.mpv',
    '.mxf',
    '.ogg',
    '.ogv',
    '.qt',
    '.rm',
    '.swf',
    '.ts',
    '.vob',
    '.webm',
    '.wmv',
];

const COMMON_IMAGE_EXTENSIONS = [
    '.apng',
    '.avif',
    '.bmp',
    '.cgm',
    '.djvu',
    '.gif',
    '.ico',
    '.jpg',
    '.jpeg',
    '.jpx',
    '.png',
    '.svg',
    '.tiff',
    '.tif',
    '.webp',
    '.heic',
    '.heif',
];

export const ALLOW_LIST_MIME_TYPES = ['image/*', 'video/*'];

export function isMimeTypeAllowed(mimeType: string): boolean {
    if (!mimeType) {
        return false;
    }
    const normalizedMimeType = mimeType.trim().toLowerCase();
    return ALLOW_LIST_MIME_TYPES.some((allowedType) =>
        normalizedMimeType.startsWith(allowedType.split('*')[0])
    );
}

/*
 * Check if media extension is allowed
 * @param extension - '.jpg'
 */
export function isMediaExtensionAllowed(extension: string): boolean {
    if (!extension) {
        return false;
    }
    const normalizedExtension = extension.trim().toLowerCase();
    return (
        COMMON_IMAGE_EXTENSIONS.includes(normalizedExtension) ||
        COMMON_VIDEO_EXTENSIONS.includes(normalizedExtension)
    );
}

/*
 * Get media extension from URL
 * http://example.com/file.jpg?height=400#anchor => .jpg
 */
export function getMediaExtensionFromUrl(url: string): string {
    try {
        if (!url) {
            return '';
        }
        const cleanUrl = url.trim();
        const pathname = new URL(cleanUrl).pathname;
        const segments = pathname.split('.');
        if (segments.length > 1 && segments[segments.length - 1] !== '') {
            const extension = segments.pop();
            return (
                '.' + extension.split('?')?.[0]?.split('#')?.[0] || ''
            ).toLowerCase();
        }
        return '';
    } catch (e) {
        return '';
    }
}

/*
 * Check if media is valid
 * @param contentType - 'image/jpeg'
 * @param downloadUri - 'http://example.com/file.jpg'
 */
export function isValidMedia(
    contentType: string,
    downloadUri: string
): boolean {
    const extensionFromUrl = getMediaExtensionFromUrl(downloadUri);
    // no file extension in url, check MIME type
    if (extensionFromUrl === '') {
        if (!isMimeTypeAllowed(contentType)) {
            return false;
        }
    } else {
        // we have a file extension, check if it's allowed
        if (!isMediaExtensionAllowed(extensionFromUrl)) {
            return false;
        }
    }
    return true;
}

export function getMediaTypeByUrl(url?: string): MediaType {
    if (!url) {
        return MediaType.Photo;
    }

    const isVideo = COMMON_VIDEO_EXTENSIONS.find((extension) =>
        url.toLowerCase().endsWith(extension)
    );
    if (isVideo) {
        return MediaType.Video;
    }

    return MediaType.Photo;
}

export function mediaSort(array: Media[] = []): Media[] {
    const sortedArray = array.sort((a: Media, b: Media) => {
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
            return a.sortOrder - b.sortOrder;
        }
        if (a.takenAt && b.takenAt) {
            return b.takenAt.getTime() - a.takenAt.getTime();
        }
        if (a.createdAt && b.createdAt) {
            return b.createdAt.getTime() - a.createdAt.getTime();
        }
        return 0;
    });
    return sortedArray;
}
